import { Controller } from "@hotwired/stimulus"
import { get } from "@rails/request.js"

// Spotlight search for the homepage. Real search now: on input (debounced) it
// GETs /find as a turbo-stream and lets Turbo swap #hp-search-results with the
// server-rendered, grouped results. This controller owns the modal shell
// (open/close, scroll lock, the "/" and ⌘K shortcuts) and the WAI-ARIA combobox
// keyboard model over whatever rows the server rendered — DOM focus stays on the
// input, the active row is tracked via aria-activedescendant.
//
// Triggers are found document-wide via [data-homepage-search-open] rather than
// Stimulus actions, because the nav is rendered by the layout, outside this
// controller's scope. That also lets an entry point be added anywhere.
export default class extends Controller {
  static targets = ["overlay", "input", "list", "count", "live"]
  static values = { url: String, debounce: { type: Number, default: 200 } }

  connect() {
    this.active = 0
    this.items = []
    this.lastTrigger = null
    this.timeout = null

    this.onDocumentClick = (event) => {
      const trigger = event.target.closest("[data-homepage-search-open]")
      if (!trigger) return
      event.preventDefault()
      this.open(trigger)
    }
    this.onDocumentKeydown = (event) => this.globalKeydown(event)

    document.addEventListener("click", this.onDocumentClick)
    document.addEventListener("keydown", this.onDocumentKeydown)

    // The idle prompt is already in the DOM (server-rendered) — index it.
    this.collect()
  }

  disconnect() {
    document.removeEventListener("click", this.onDocumentClick)
    document.removeEventListener("keydown", this.onDocumentKeydown)
    clearTimeout(this.timeout)
    this.unlockScroll()
  }

  // --- open / close ---------------------------------------------------------

  open(trigger = null) {
    this.lastTrigger = trigger
    this.overlayTarget.hidden = false
    this.lockScroll()
    this.inputTarget.value = ""
    this.request() // reset to the idle state (q="")
    // Focus after paint so mobile reliably raises the keyboard.
    requestAnimationFrame(() => this.inputTarget.focus())
  }

  close() {
    if (!this.isOpen) return
    this.overlayTarget.hidden = true
    this.unlockScroll()
    if (this.lastTrigger) this.lastTrigger.focus()
    this.lastTrigger = null
  }

  backdrop(event) {
    if (event.target === this.overlayTarget) this.close()
  }

  get isOpen() {
    return !this.overlayTarget.hidden
  }

  lockScroll() {
    this.previousOverflow = document.body.style.overflow
    document.body.style.overflow = "hidden"
  }

  unlockScroll() {
    document.body.style.overflow = this.previousOverflow || ""
  }

  // --- fetch ----------------------------------------------------------------

  // Debounced entry point (bound to input). Coalesces keystrokes into one GET.
  search() {
    clearTimeout(this.timeout)
    this.timeout = setTimeout(() => this.request(), this.debounceValue)
  }

  // Turbo applies the returned stream to #hp-search-results; we then re-collect
  // the rows for keyboard nav. On a network error we leave the last results up.
  async request() {
    const query = this.inputTarget.value.trim()
    try {
      await get(`${this.urlValue}?q=${encodeURIComponent(query)}`, { responseKind: "turbo-stream" })
    } catch (_error) {
      return
    }
    requestAnimationFrame(() => {
      this.collect()
      this.announce(query)
    })
  }

  // Re-read the server-rendered rows, id them for aria-activedescendant, reset
  // the highlight to the top. [data-nav] is everything the keyboard can reach;
  // [data-row] is the subset that is an actual result — the "See all" links are
  // navigable but are not results, and counting them overstated the total.
  collect() {
    this.items = Array.from(this.listTarget.querySelectorAll("[data-nav]"))
    this.items.forEach((element, position) => { element.id = `hp-search-${position}` })
    this.active = 0
    this.paint()
  }

  get resultCount() {
    return this.items.filter((element) => element.dataset.row).length
  }

  announce(query) {
    const total = this.resultCount
    const label = query ? (total ? `${total} result${total === 1 ? "" : "s"}` : "No results") : ""
    if (this.hasCountTarget) this.countTarget.textContent = label
    if (query && this.hasLiveTarget) this.liveTarget.textContent = `${label} for ${query}`
  }

  // --- keyboard -------------------------------------------------------------

  // All key handling lives on the document — NOT the input. Binding it to the
  // input means every key dies the moment focus drifts off it (clicking the
  // panel chrome is enough), which makes Esc look broken.
  globalKeydown(event) {
    if (this.isOpen) {
      this.keydown(event)
      return
    }
    const typing = /^(INPUT|TEXTAREA|SELECT)$/.test(document.activeElement.tagName) ||
      document.activeElement.isContentEditable
    // event.key, not event.code — "/" sits elsewhere on non-US layouts.
    const slash = event.key === "/" && !typing
    const command = event.key === "k" && (event.metaKey || event.ctrlKey)
    if (!slash && !command) return
    event.preventDefault()
    this.open(event.target.closest("[data-homepage-search-open]"))
  }

  keydown(event) {
    const navigation = ["Escape", "ArrowDown", "ArrowUp", "Home", "End", "Enter"]
    if (!navigation.includes(event.key)) {
      // Typing anywhere while open should land in the box.
      if (document.activeElement === this.inputTarget) return
      this.inputTarget.focus()
      const printable = event.key.length === 1 && !event.metaKey && !event.ctrlKey && !event.altKey
      if (!printable) return
      event.preventDefault()
      this.inputTarget.value += event.key
      this.search()
      return
    }

    switch (event.key) {
      case "Escape":
        event.preventDefault()
        this.close()
        break
      case "ArrowDown":
        event.preventDefault()
        this.move(1)
        break
      case "ArrowUp":
        event.preventDefault()
        this.move(-1)
        break
      case "Home":
        event.preventDefault()
        this.highlight(0)
        break
      case "End":
        event.preventDefault()
        this.highlight(this.items.length - 1)
        break
      case "Enter":
        event.preventDefault()
        this.select(this.items[this.active])
        break
    }
  }

  move(step) {
    if (!this.items.length) return
    // Wrap at both ends — the list loops rather than dead-ending.
    this.highlight((this.active + step + this.items.length) % this.items.length)
  }

  highlight(position) {
    this.active = position
    this.paint()
  }

  paint() {
    this.items.forEach((element, position) => {
      const on = position === this.active
      element.dataset.active = on ? "true" : "false"
      element.setAttribute("aria-selected", on ? "true" : "false")
      if (!on) return
      this.inputTarget.setAttribute("aria-activedescendant", element.id)
      element.scrollIntoView({ block: "nearest" })
    })
    if (!this.items.length) this.inputTarget.removeAttribute("aria-activedescendant")
  }

  pointerMove(event) {
    const row = event.target.closest("[data-nav]")
    if (!row) return
    const position = this.items.indexOf(row)
    if (position >= 0 && position !== this.active) this.highlight(position)
  }

  // --- selection ------------------------------------------------------------

  // Rows are real links now — follow the highlighted row's href.
  select(element) {
    if (!element) return
    this.close()
    window.location.href = element.href
  }
}
